import { useEffect } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, Sparkles, Check } from "lucide-react";
import { Footer } from "../components/landing/Footer";

const TRIAL_URL = "https://pay.rev.cat/kaooarqpwiwxiwlj";
const ease = [0.22, 1, 0.36, 1];

const BENEFITS = [
  "Feel your body settle and your mind go quiet within your first ten-minute session",
  "Reconnect with your radiance and high self-worth — stop settling for less and start showing up in your life in alignment with your vision",
  "Nervous system regulation and visualization work, paired together so you feel safe as you move into the unknown",
];

const fade = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, ease, delay: 0.15 + i * 0.1 },
  }),
};

export default function Offer() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="grain relative min-h-screen overflow-hidden bg-[#0a0a0c]" data-testid="offer-page">
      {/* glows */}
      <div className="pointer-events-none absolute inset-0 -z-0">
        <div className="orb orb-violet absolute -top-32 left-1/4 h-[34rem] w-[34rem] rounded-full bg-[#7c3aed] blur-[150px] opacity-30" />
        <div className="orb orb-peach absolute top-10 right-0 h-[30rem] w-[30rem] rounded-full bg-[#ffb7b2] blur-[150px] opacity-25" />
        <div className="orb orb-indigo absolute bottom-0 left-0 h-[28rem] w-[28rem] rounded-full bg-[#4f46e5] blur-[160px] opacity-20" />
      </div>

      <div className="relative z-10 mx-auto grid min-h-screen max-w-6xl items-center gap-14 px-6 py-24 md:grid-cols-[1.1fr_0.9fr] md:gap-10 lg:gap-16">
        {/* copy */}
        <div>
          <motion.div variants={fade} initial="hidden" animate="show" className="flex justify-center md:justify-start">
            <span className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-4 py-1.5 text-xs uppercase tracking-[0.3em] text-white/70">
              <Sparkles className="h-3.5 w-3.5 text-[#ffb7b2]" /> A special invitation
            </span>
          </motion.div>

          <motion.h1
            variants={fade}
            custom={1}
            initial="hidden"
            animate="show"
            className="mt-8 text-center font-display text-4xl font-thin leading-[1.08] tracking-tight text-white md:text-left md:text-6xl"
          >
            Come back to
            <br />
            <span className="italic text-white/85">your radiance.</span>
          </motion.h1>

          <motion.div
            variants={fade}
            custom={2}
            initial="hidden"
            animate="show"
            className="mx-auto mt-10 max-w-xl space-y-3.5 md:mx-0"
            data-testid="offer-benefits"
          >
            {BENEFITS.map((b, i) => (
              <div
                key={i}
                className="flex items-start gap-3.5 rounded-2xl border border-white/10 bg-white/[0.03] p-5 backdrop-blur-xl"
                data-testid={`offer-benefit-${i + 1}`}
              >
                <span className="mt-0.5 grid h-6 w-6 shrink-0 place-items-center rounded-full bg-white/5 text-[#ffb7b2]">
                  <Check className="h-3.5 w-3.5" />
                </span>
                <p className="text-sm leading-relaxed text-white/70 md:text-base">{b}</p>
              </div>
            ))}
          </motion.div>

          <motion.p
            variants={fade}
            custom={3}
            initial="hidden"
            animate="show"
            className="mt-10 text-center text-base text-white/70 md:text-left md:text-lg"
            data-testid="offer-code-note"
          >
            Use code{" "}
            <span className="rounded-md border border-white/20 bg-white/10 px-2 py-0.5 font-display font-semibold tracking-[0.15em] text-white">
              GRATEFUL
            </span>{" "}
            for one month free.
          </motion.p>

          <motion.div
            variants={fade}
            custom={4}
            initial="hidden"
            animate="show"
            className="mt-8 flex flex-col items-center md:items-start"
          >
            <a
              href={TRIAL_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="group inline-flex items-center gap-2.5 rounded-full bg-white px-9 py-4 text-sm font-medium text-black transition-transform duration-300 hover:scale-105 hover:shadow-[0_0_30px_rgba(255,255,255,0.35)]"
              data-testid="offer-cta-button"
            >
              Claim your free month
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </a>
            <p className="mt-5 text-center text-xs text-white/40 md:text-left" data-testid="offer-email-note">
              (please sign up with an email you currently have access to)
            </p>
          </motion.div>
        </div>

        {/* phone mockup */}
        <motion.div
          initial={{ opacity: 0, y: 60 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease, delay: 0.35 }}
          className="relative mx-auto w-full max-w-[16rem] md:max-w-[17rem]"
          data-testid="offer-phone"
        >
          <div className="pointer-events-none absolute -inset-10 -z-10 rounded-full bg-[#7c3aed] blur-[110px] opacity-25" />
          <div className="relative aspect-[9/19.5] w-full overflow-hidden rounded-[2.6rem] border border-white/12 bg-black p-1.5 shadow-[0_30px_80px_-20px_rgba(79,70,229,0.35)]">
            <img
              src="/app-shots/manifest.webp"
              alt="Radiate app — Manifest sessions including Tap into Lucky Energy"
              className="h-full w-full rounded-[2.2rem] object-cover object-top"
            />
          </div>
          <p className="mt-6 text-center text-xs uppercase tracking-[0.25em] text-white/40">
            Inside the app
          </p>
        </motion.div>
      </div>

      <Footer />
    </div>
  );
}
